import React, { useState } from 'react';
import { KeyRound, Loader2, Mail, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authApi } from '@/lib/auth_api';
import { useAuth } from '@/lib/auth_context';
import { FONTS, P } from '@/components/landing/scriptorium/palette';

export const PrivateEmailSignIn = ({ onForgot, onCreateAccount }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [davidOpen, setDavidOpen] = useState(false);
  const [davidPassword, setDavidPassword] = useState('');
  const [davidBusy, setDavidBusy] = useState(false);

  const davidSubmit = async (event) => {
    event.preventDefault();
    setDavidBusy(true);
    try {
      await authApi.davidSuiteLogin(davidPassword);
      toast.success('Signed in as David — Suite access.');
      await refresh();
      navigate('/app');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not sign in as David.');
    } finally {
      setDavidBusy(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await authApi.passwordLogin(email.trim(), password);
      toast.success(`${t('login.welcomeBack')}, ${result.user?.name || result.user?.email}.`);
      await refresh();
      navigate('/app');
    } catch (error) {
      toast.error(error?.response?.data?.detail || t('login.emailSignInFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <form onSubmit={submit} className="space-y-2.5 text-left" data-testid="login-private-email-form">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: P.oxblood, fontFamily: FONTS.display }}>
        <Mail className="h-3.5 w-3.5" />
        {t('login.privateEmail')}
      </div>
      <label className="block">
        <span className="sr-only">{t('login.emailAddress')}</span>
        <input
          type="email"
          required
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={t('login.emailAddress')}
          data-testid="login-private-email-input"
          className="h-10 w-full rounded-md border px-3 text-[13px] outline-none transition focus:border-[#E57A1C] focus:ring-0"
          style={{ backgroundColor: P.paper, borderColor: `${P.gold}88`, color: P.ink }}
        />
      </label>
      <label className="block">
        <span className="sr-only">{t('login.password')}</span>
        <input
          type="password"
          required
          autoComplete="current-password"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder={t('login.password')}
          data-testid="login-private-password-input"
          className="h-10 w-full rounded-md border px-3 text-[13px] outline-none transition focus:border-[#E57A1C] focus:ring-0"
          style={{ backgroundColor: P.paper, borderColor: `${P.gold}88`, color: P.ink }}
        />
      </label>
      <button
        type="submit"
        disabled={busy}
        data-testid="login-private-email-submit"
        className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full border text-[12px] font-semibold transition hover:brightness-110 active:scale-[0.98] disabled:opacity-60"
        style={{
          borderColor: P.oxblood,
          color:       P.paper,
          background:  `linear-gradient(180deg, ${P.oxbloodHi} 0%, ${P.oxblood} 100%)`,
          boxShadow:   `0 8px 22px -10px ${P.oxblood}88`,
          fontFamily:  FONTS.display,
        }}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
        {busy ? t('login.signingIn') : t('login.signInWithEmail')}
      </button>
      <div className="flex flex-wrap items-center justify-between gap-2 text-[10.5px]">
        <button type="button" onClick={onForgot} data-testid="login-private-email-forgot" className="font-semibold hover:underline" style={{ color: P.oxblood }}>
          {t('login.forgotPassword')}
        </button>
        <button type="button" onClick={onCreateAccount} data-testid="login-private-email-create" className="font-semibold hover:underline" style={{ color: P.oxblood }}>
          {t('login.createAccount')}
        </button>
      </div>
      <p className="text-[10.5px] leading-relaxed" style={{ color: P.inkSoft }} data-testid="login-private-email-privacy">
        {t('login.emailPrivacy')}
      </p>
    </form>

    <div className="mt-3 border-t pt-3 text-left" style={{ borderColor: `${P.gold}55` }}>
      {!davidOpen ? (
        <button
          type="button"
          onClick={() => setDavidOpen(true)}
          data-testid="login-continue-as-david"
          className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-full border text-[12px] font-semibold transition hover:brightness-110 active:scale-[0.98]"
          style={{
            borderColor: P.oxblood,
            color:       P.paper,
            background:  `linear-gradient(180deg, ${P.oxbloodHi} 0%, ${P.oxblood} 100%)`,
            boxShadow:   `0 8px 22px -10px ${P.oxblood}88`,
            fontFamily:  FONTS.display,
          }}
        >
          <ShieldCheck className="h-4 w-4" /> Staff Entrance
        </button>
      ) : (
        <form onSubmit={davidSubmit} className="space-y-2" data-testid="login-david-form">
          <label className="block">
            <span className="sr-only">Admin password</span>
            <input
              type="password"
              required
              autoFocus
              autoComplete="current-password"
              value={davidPassword}
              onChange={(event) => setDavidPassword(event.target.value)}
              placeholder="David's admin password"
              data-testid="login-david-password-input"
              className="h-10 w-full rounded-md border px-3 text-[13px] outline-none transition focus:border-[#E57A1C] focus:ring-0"
              style={{ backgroundColor: P.paper, borderColor: `${P.gold}88`, color: P.ink }}
            />
          </label>
          <button
            type="submit"
            disabled={davidBusy}
            data-testid="login-david-submit"
            className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full border text-[12px] font-semibold transition hover:brightness-110 active:scale-[0.98] disabled:opacity-60"
            style={{
              borderColor: P.oxblood,
              color:       P.paper,
              background:  `linear-gradient(180deg, ${P.oxbloodHi} 0%, ${P.oxblood} 100%)`,
              boxShadow:   `0 8px 22px -10px ${P.oxblood}88`,
              fontFamily:  FONTS.display,
            }}
          >
            {davidBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {davidBusy ? t('login.signingIn') : 'Enter the Suite'}
          </button>
        </form>
      )}
    </div>
    </>
  );
};